import { formatKitchenTierLabel } from "@/lib/kitchenTierLabel";

/**
 * Scaled per-order ingredient sheet for a baker task.
 *
 * Both baker layouts rendered an identical copy of this markup under different
 * headings — the baker delivery layout swaps its heading and sub-heading for a
 * cheesecake, the default layout never does. The headings are therefore props
 * supplied by the call site rather than derived here, so a layout cannot lose
 * its wording without the call site changing.
 */

function CakeBlockLabel({ label }) {
  return (
    <div className="rounded-xl border-2 border-[#EE2677]/35 bg-[#FCE7F3] px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wide text-[#C2185B]">Cake</p>
      <p className="text-sm font-extrabold leading-tight text-[#2D2D2D]">{label}</p>
    </div>
  );
}

function IngredientList({ title, subtitle, items, accent }) {
  const divider = accent.divider || "border-[#EBB3A9]/40";
  if (!items?.length) {
    return (
      <div className={`overflow-hidden rounded-2xl border ${accent.border} ${accent.bg}`}>
        <div className={`border-b px-3 py-2 ${divider} ${accent.bg}`}>
          <p className={`text-xs font-extrabold uppercase tracking-wide ${accent.title}`}>{title}</p>
        </div>
        <p className="px-3 py-3 text-xs text-[#888]">None for this order</p>
      </div>
    );
  }
  return (
    <div className={`overflow-hidden rounded-2xl border ${accent.border} ${accent.bg}`}>
      <div className={`flex items-baseline justify-between gap-2 border-b px-3 py-2 ${divider}`}>
        <p className={`text-xs font-extrabold uppercase tracking-wide ${accent.title}`}>{title}</p>
        <p className={`text-[11px] font-semibold ${accent.subtitle}`}>{subtitle}</p>
      </div>
      <table className="w-full border-collapse text-sm">
        <tbody>
          {items.map((item, i) => (
            <tr key={`${item.name}-${i}`} className={`border-b ${divider} last:border-b-0`}>
              <td className={`border-r px-3 py-2 align-top font-semibold leading-tight text-[#2D2D2D] ${divider}`}>
                {item.name}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right align-top font-bold leading-tight text-[#2D2D2D]">
                {item.amount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const DRY_ACCENT = {
  border: "border-amber-200",
  bg: "bg-amber-50/80",
  title: "text-amber-900",
  subtitle: "text-amber-700",
  divider: "border-amber-200/70",
};

const WET_ACCENT = {
  border: "border-sky-200",
  bg: "bg-sky-50/80",
  title: "text-sky-900",
  subtitle: "text-sky-700",
  divider: "border-sky-200/70",
};

export default function IngredientBreakdownCard({ info, title, subtitle }) {
  return (
    <div className="mt-4 rounded-2xl border-2 border-[#EE2677]/25 bg-gradient-to-b from-white to-[#FFF7FA] p-3 shadow-sm">
      <p className="text-sm font-extrabold uppercase tracking-wide text-[#2D2D2D]">{title}</p>
      <p className="mt-0.5 text-xs text-[#666]">{subtitle}</p>

      <div className="mt-3 space-y-3">
        {info.ingredient_breakdown.blocks.map((block, index) => (
          <div
            key={`${block.label}-${index}`}
            className="overflow-hidden rounded-2xl border border-[#EBB3A9]/50 bg-white"
          >
            <div className="border-b border-[#EBB3A9]/40 bg-[#EE2677]/10 px-3 py-2">
              <CakeBlockLabel label={block.label} />
              {!info?.is_cheesecake && !block.is_cheesecake ? (
                <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-semibold">
                  {formatKitchenTierLabel(info?.tiers) ? (
                    <span className="rounded-full border-2 border-violet-500 bg-violet-100 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wide text-violet-900">
                      {formatKitchenTierLabel(info.tiers)}
                    </span>
                  ) : null}
                  <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-900">
                    Base: {block.base_flour || info?.base_flour || "Maida"}
                  </span>
                  <span className="rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-sky-900">
                    Sweetener: {block.sweetener || info?.sweetener || "Sugar"}
                  </span>
                  {(block.base_fat || info?.base_fat) === "Butter" ? (
                    <span className="rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-rose-900">
                      Butter
                      {(block.fat_upsell || 0) > 0
                        ? ` (+₹${Math.round(block.fat_upsell)})`
                        : ""}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-2 p-2">
              <IngredientList
                title="Dry"
                subtitle={`${block.dry_grams ?? 0} g`}
                items={block.dry_items}
                accent={DRY_ACCENT}
              />
              <IngredientList
                title="Wet"
                subtitle={`${block.wet_grams ?? 0} g`}
                items={block.wet_items}
                accent={WET_ACCENT}
              />
            </div>

            <div className="flex flex-wrap gap-2 border-t border-[#EBB3A9]/30 bg-[#FAFAFA] px-3 py-2 text-[11px] font-semibold text-[#555]">
              <span>Dry: {block.dry_grams ?? 0} g</span>
              <span>·</span>
              <span>Wet: {block.wet_grams ?? 0} g</span>
              <span>·</span>
              <span className="text-[#C2185B]">Total: {block.total_grams ?? 0} g</span>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-3 rounded-xl bg-[#EE2677]/10 px-3 py-2 text-center text-xs font-bold text-[#C2185B]">
        Grand total input: {info?.ingredient_breakdown?.grand_total_grams || 0} g
      </p>
    </div>
  );
}
